import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { env } from "../../../config/env";
import { productsService } from "../../../services/products.service";

function toImageUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  const base = env?.CATALOG_BASE_URL || process.env.REACT_APP_CATALOG_BASE_URL || "";
  return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
}

function Field({ label, value }) {
  return (
    <div className="rounded-[14px] border border-slate-200 bg-white p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-2 break-words text-[14px] font-semibold text-slate-700">{value == null || value === "" ? "-" : value}</div>
    </div>
  );
}

function money(v, currency = "INR") {
  if (v == null || v === "") return "-";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 2 }).format(n);
  } catch {
    return `${currency} ${n}`;
  }
}

function fmtDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString();
}

function VariantChip({ variant, active, onClick }) {
  const label = variant?.variantName || variant?.packLabel || variant?.variantValue || "Variant";
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-2 rounded-full border px-3 py-2 text-[12px] font-semibold transition " +
        (active ? "border-[#d86a78] bg-[#fff0f3] text-[#d86a78]" : "border-slate-200 bg-white text-slate-600 hover:border-[#f2c6cc]")
      }
    >
      <span>{label}</span>
      {variant?.isDefault ? <span className="rounded-full bg-[#ffdfe5] px-2 py-[2px] text-[10px] text-[#d86a78]">Default</span> : null}
    </button>
  );
}

export default function ProductDetailsPage() {
  const nav = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const backToProducts = location.state?.returnTo || "/admin/dashboard/products";
  const [state, setState] = useState({ loading: true, error: "", item: null });
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState("");
  const [selectedImage, setSelectedImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!id) return;
      try {
        setState({ loading: true, error: "", item: null });
        const res = await productsService.getProductById(id);
        const item = res?.item || res?.product || res?.data?.item || null;
        if (!alive) return;
        setState({ loading: false, error: "", item });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, error: e?.message || "Failed to load product details", item: null });
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const item = state.item;
  const parentImages = useMemo(() => Array.isArray(item?.images) ? item.images.map((img) => ({ ...img, url: toImageUrl(img?.url) })) : [], [item]);
  const variants = useMemo(() => Array.isArray(item?.variants) ? item.variants : [], [item]);

  useEffect(() => {
    const first = variants.find((v) => v?.isDefault) || variants[0] || null;
    setSelectedVariantId(first?.id || "");
  }, [item?.id, variants]);

  const selectedVariant = useMemo(() => variants.find((v) => String(v?.id) === String(selectedVariantId)) || variants.find((v) => v?.isDefault) || variants[0] || null, [variants, selectedVariantId]);

  const galleryImages = useMemo(() => {
    const source = Array.isArray(selectedVariant?.images) && selectedVariant.images.length ? selectedVariant.images : parentImages;
    return source.map((img) => ({ ...img, url: toImageUrl(img?.url || img?.imageUrl || img?.image_url || "") })).filter((img) => img.url);
  }, [selectedVariant, parentImages]);

  useEffect(() => {
    const next = galleryImages.find((img) => img?.isPrimary)?.url || galleryImages[0]?.url || "";
    setSelectedImage(next);
  }, [selectedVariantId, galleryImages]);

  async function handleDelete() {
    if (!item?.id) return;
    try {
      setDeleting(true);
      await productsService.deleteProduct(item.id);
      nav(backToProducts);
    } catch (e) {
      setState((s) => ({ ...s, error: e?.message || "Delete failed" }));
    } finally {
      setDeleting(false);
      setDeletePopup(false);
    }
  }

  return (
    <div className="w-full">
      <ConfirmModal
        open={deletePopup}
        icon="trash"
        variant="confirm"
        title={`Are you Sure You want to Delete ${item?.name || ""}`}
        description="This will remove this product from the system. Would you like to Proceed?"
        onClose={() => { if (!deleting) setDeletePopup(false); }}
        onConfirm={handleDelete}
        cancelText="NO"
        confirmText={deleting ? "Deleting…" : "YES"}
      />

      {previewOpen && selectedImage ? (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 px-4 py-6" onClick={() => setPreviewOpen(false)}>
          <div className="relative max-h-[90vh] w-full max-w-[900px] rounded-[16px] bg-white p-3 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setPreviewOpen(false)} className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/75">×</button>
            <div className="flex max-h-[82vh] items-center justify-center overflow-hidden rounded-[12px] bg-[#f8f8f8]">
              <img src={selectedImage} alt={item?.name || "product"} className="max-h-[82vh] w-auto max-w-full object-contain" />
            </div>
          </div>
        </div>
      ) : null}

      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => nav(backToProducts)} className="inline-flex h-[40px] w-[40px] items-center justify-center rounded-full border border-[#f2c6cc] bg-white text-[#d86a78] hover:bg-[#fff4f6]">
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-[25px] font-bold tracking-wide text-[#d86a78]">PRODUCT DETAILS</h1>
        </div>
      </div>

      {state.error ? <div className="mb-4 rounded-[10px] border border-rose-200 bg-rose-50 px-4 py-3 text-[12px] text-rose-700">{state.error}</div> : null}

      {state.loading ? (
        <div className="flex h-[360px] items-center justify-center rounded-[20px] border border-[#f2c6cc] bg-white text-[14px] font-semibold text-slate-500">Loading product details...</div>
      ) : !item ? (
        <div className="flex h-[360px] items-center justify-center rounded-[20px] border border-[#f2c6cc] bg-white text-[14px] font-semibold text-rose-600">Product details not found.</div>
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[380px_minmax(0,1fr)]">
          <div className="rounded-[14px] bg-[#f8f8f8] p-4 sm:p-5">
            <div className="rounded-[14px] bg-white p-4 shadow-sm">
              <div className="overflow-hidden rounded-[12px] bg-[#f6f6f6]">
                {selectedImage ? (
                  <button type="button" onClick={() => setPreviewOpen(true)} className="block w-full">
                    <img src={selectedImage} alt={item?.name || "product"} className="h-[290px] w-full object-cover" />
                  </button>
                ) : (
                  <div className="flex h-[290px] items-center justify-center text-[12px] text-slate-400">No image</div>
                )}
              </div>

              {galleryImages.length > 1 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {galleryImages.map((img) => (
                    <button key={img.id || img.url} type="button" onClick={() => setSelectedImage(img.url)} className={"overflow-hidden rounded-[10px] border p-[2px] " + (selectedImage === img.url ? "border-[#d86a78]" : "border-slate-200")}>
                      <img src={img.url} alt="thumb" className="h-[48px] w-[48px] object-cover" />
                    </button>
                  ))}
                </div>
              ) : null}

              <div className="mt-5 grid grid-cols-3 gap-3 text-[12px]">
                <div>
                  <div className="text-[#d86a78]">Product Name</div>
                  <div
                    title={item?.name}
                    className="mt-1 max-w-[220px] truncate text-slate-600 cursor-pointer"
                  >
                    {item?.name || "-"}
                  </div>
                </div>
                <div><div className="text-[#d86a78]">SKU</div><div className="mt-1 text-slate-600">{item?.sku || "-"}</div></div>
                <div><div className="text-[#d86a78]">Barcode</div><div className="mt-1 text-slate-600">{item?.barcode || "-"}</div></div>
                <div><div className="text-[#d86a78]">Status</div><div className="mt-1 text-slate-600">{item?.status || "-"}</div></div>
                <div><div className="text-[#d86a78]">Is Active</div><div className="mt-1 text-slate-600">{String(item?.isActive ?? "-")}</div></div>
                <div><div className="text-[#d86a78]">Slug</div><div className="mt-1 text-slate-600">{item?.slug || "-"}</div></div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3">
                <button type="button" onClick={() => nav(`/admin/dashboard/products/${item?.id}/edit`)} className="h-[38px] rounded-[10px] bg-slate-500 px-4 text-[13px] font-semibold text-white">Edit Product</button>
                <button type="button" onClick={() => setDeletePopup(true)} className="h-[38px] rounded-[10px] bg-[#d86a78] px-4 text-[13px] font-semibold text-white">Delete product</button>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5">
              <h3 className="text-[18px] font-bold text-[#d86a78]">Product Information</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label="Category" value={item?.categoryName} />
                <Field label="Brand" value={item?.brandName} />
                <Field label="Unit" value={item?.unitSymbol || item?.unitName} />
                <Field label="Tax Percent" value={item?.taxPercent} />
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Short Description" value={item?.shortDescription} />
                <Field label="Description" value={item?.description} />
              </div>
            </div>

            <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5">
              <h3 className="text-[18px] font-bold text-[#d86a78]">Actual Product Variants</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {variants.map((variant) => (
                  <VariantChip key={variant?.id || variant?.variantSku || variant?.variantName} variant={variant} active={String(variant?.id) === String(selectedVariant?.id)} onClick={() => setSelectedVariantId(variant?.id || "")} />
                ))}
              </div>

              {selectedVariant ? (
                <div className="mt-5 rounded-[16px] border border-[#f2c6cc] bg-[#fffafb] p-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    <Field label="Variant Name" value={selectedVariant?.variantName} />
                    <Field label="Variant Group" value={selectedVariant?.variantGroup} />
                    <Field label="Variant Value" value={selectedVariant?.variantValue} />
                    <Field label="Pack Label" value={selectedVariant?.packLabel} />
                    <Field label="Variant SKU" value={selectedVariant?.variantSku} />
                    <Field label="Variant Barcode" value={selectedVariant?.barcode} />
                    <Field label="Qty / Pack" value={[selectedVariant?.unitValue, selectedVariant?.unitSymbol].filter(Boolean).join(" ")} />
                    <Field label="Weight" value={[selectedVariant?.weightValue, selectedVariant?.weightUnitSymbol].filter(Boolean).join(" ")} />
                    <Field label="MRP" value={money(selectedVariant?.mrp)} />
                    <Field label="Selling Price" value={money(selectedVariant?.sellingPrice)} />
                    <Field label="Default" value={selectedVariant?.isDefault ? "Yes" : "No"} />
                    <Field label="Active" value={selectedVariant?.isActive ? "Yes" : "No"} />
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                    <Field label="Variant Short Description" value={selectedVariant?.shortDescription} />
                    <Field label="Variant Description" value={selectedVariant?.description} />
                  </div>
                </div>
              ) : (
                <div className="mt-5 rounded-[12px] border border-dashed border-slate-300 px-4 py-6 text-[12px] text-slate-400">No variants found.</div>
              )}
            </div>

            <div className="rounded-[18px] border border-[#f2c6cc] bg-white p-5">
              <h3 className="text-[18px] font-bold text-[#d86a78]">System Information</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Created At" value={fmtDate(item?.createdAt || item?.created_at)} />
                <Field label="Updated At" value={fmtDate(item?.updatedAt || item?.updated_at)} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
